import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRealtimeStore } from "../../lib/store.js";
import { ContextMenuPositioner } from "../../components/context-menu.js";
import { SchedulePickerModal } from "./schedule-picker-modal.js";
import {
  RIGHT_PANEL_WIDTH_MAX,
  RIGHT_PANEL_WIDTH_MIN,
  useUiPrefs,
  type RightPanelTab
} from "../../lib/ui-prefs.js";
import { ChatAvatar } from "../../components/chat-avatar.js";
import { countryLabel } from "../../lib/country.js";
import { avatarGradientClass } from "../dialogs/dialog-list.js";
import { useTrustTemplates, type TrustFilterKind } from "./use-trust-templates.js";
import type { MessageContent } from "@tg/shared";
import { httpBaseFromWsUrl } from "../../lib/auth-api.js";
import { formatFullDateTime, formatPeerStatus, type TimeZonePref } from "../../lib/datetime.js";
import { copyTextToClipboard } from "../../lib/clipboard.js";
import { internalApi } from "../../lib/internal-api.js";
import { ChatMediaSection } from "./chat-media-section.js";
import { ClientBotWalletPanel } from "./client-bot-wallet-panel.js";
import {
  DepositTrackerModal,
  type TrackerModalPrefill
} from "../../components/deposit-tracker-modal.js";
import { trackerApi, type TrackerClient, type TrackerOption } from "../../lib/tracker-api.js";

const TABS: { id: RightPanelTab; label: string }[] = [
  { id: "info", label: "Инфо" },
  { id: "templates", label: "Шаблоны" },
  { id: "scheduled", label: "Отложенные" },
  { id: "deleted", label: "Удалённые" },
  { id: "payments", label: "Способы оплаты" }
];

// Пресеты таймера автоудаления — как в оригинальном TG (перенесено из
// старой карточки профиля, удалённой в PR #7).
const AUTO_DELETE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: "Выключено" },
  { value: 86_400, label: "1 день" },
  { value: 604_800, label: "1 неделя" },
  { value: 2_678_400, label: "1 месяц" }
];

// Стабильная пустая ссылка: селекторы Zustand НЕ должны возвращать новый
// [] каждый рендер — иначе useSyncExternalStore зацикливает ре-рендер.
const EMPTY_SCHEDULED: never[] = [];

// Кэш контекста трекера на вкладку: статусы клиента не меняются в рантайме,
// а бегать в getContext на каждый чат бессмысленно. F5 обновит.
let cachedTrackerContextPromise: Promise<{
  statuses: TrackerOption[];
  enabled: boolean;
  hasBinding: boolean;
}> | null = null;

const loadTrackerContextOnce = (wsUrl: string): NonNullable<typeof cachedTrackerContextPromise> => {
  if (!cachedTrackerContextPromise) {
    cachedTrackerContextPromise = trackerApi
      .getContext(wsUrl)
      .then((ctx) => ({
        statuses: ctx.statuses,
        enabled: ctx.enabled,
        hasBinding: ctx.binding !== null
      }))
      .catch((cause) => {
        cachedTrackerContextPromise = null;
        throw cause;
      });
  }
  return cachedTrackerContextPromise;
};

type TrackerPanelState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "disabled" }
  | { kind: "absent" }
  | { kind: "present"; clientId: number; status: number };

export const ChatSidePanel = () => {
  const open = useUiPrefs((s) => s.rightPanelOpen);
  const tab = useUiPrefs((s) => s.rightPanelTab);
  const width = useUiPrefs((s) => s.rightPanelWidth);
  const setTab = useUiPrefs((s) => s.setRightPanelTab);
  const setWidth = useUiPrefs((s) => s.setRightPanelWidth);
  const uiScale = useUiPrefs((s) => (s.uiScale > 0 ? s.uiScale : 1));

  const selectedChatId = useRealtimeStore((s) => s.selectedChatId);
  const chat = useRealtimeStore((s) =>
    s.selectedChatId ? s.chats[s.selectedChatId] ?? null : null
  );
  const profile = useRealtimeStore((s) =>
    s.selectedChatId ? s.profilesByChat[s.selectedChatId] ?? null : null
  );
  const dialogLists = useRealtimeStore((s) => s.dialogLists);
  const scheduled = useRealtimeStore((s) =>
    s.selectedChatId ? s.scheduledByChat[s.selectedChatId] ?? EMPTY_SCHEDULED : EMPTY_SCHEDULED
  );
  const chatMessages = useRealtimeStore((s) =>
    s.selectedChatId ? s.messagesByChat[s.selectedChatId] ?? EMPTY_SCHEDULED : EMPTY_SCHEDULED
  );
  const deletedMessages = useMemo(
    () => chatMessages.filter((m) => m.isDeleted),
    [chatMessages]
  );
  const loadScheduledMessages = useRealtimeStore((s) => s.loadScheduledMessages);
  const sendScheduledNow = useRealtimeStore((s) => s.sendScheduledNow);
  const deleteScheduledMessage = useRealtimeStore((s) => s.deleteScheduledMessage);
  const rescheduleMessage = useRealtimeStore((s) => s.rescheduleMessage);
  const setChatInternalTags = useRealtimeStore((s) => s.setChatInternalTags);
  const accountId = useRealtimeStore((s) => s.accountId);
  const wsUrl = useRealtimeStore((s) => s.wsUrl);
  const appRole = useRealtimeStore((s) => s.appRole);
  const requestProfile = useRealtimeStore((s) => s.requestProfile);
  const setChatAutoDeleteTime = useRealtimeStore((s) => s.setChatAutoDeleteTime);
  // Username / телефон юзера — только управляющим (как в старой карточке
  // профиля): саппорт не должен деанонимизировать клиента. ID виден всем —
  // он нужен саппорту для трекера депозитов.
  const isSupportRole = appRole === "support";
  const timeZone: TimeZonePref = useUiPrefs((s) => s.timeZone);
  const openTrustManage = useUiPrefs((s) => s.openTrustManage);
  const isAdmin = appRole === "admin" || appRole === "geo_admin";

  // Реальные шаблоны/траст/оплата — общий хук (то же ядро, что в старой панели).
  const trust = useTrustTemplates(open && (tab === "templates" || tab === "payments"));

  // Локальный ввод нового тега.
  const [tagDraft, setTagDraft] = useState("");
  const [tagInputOpen, setTagInputOpen] = useState(false);
  // Редактор «Изменить перед отправкой»: assetId + правимый текст/подпись.
  const [templateEditor, setTemplateEditor] = useState<{ assetId: string; text: string } | null>(
    null
  );
  // Раскрытие раздела «Общие медиа» (Что в чате) inline в табе «Инфо».
  const [mediaOpen, setMediaOpen] = useState(false);

  // Меню три-точки на карточке отложенного (правая панель, вкладка
  // «Отложенные»). Действия — те же, что в модалке отложенных: отправить
  // сейчас, изменить время (через SchedulePickerModal), удалить.
  const [scheduledMenu, setScheduledMenu] = useState<
    { x: number; y: number; messageId: string } | null
  >(null);
  const [scheduledPicker, setScheduledPicker] = useState<
    { messageId: string; initialAt?: string | undefined } | null
  >(null);

  // Теги: оптимистично в стор + сохранение на сервер (как делала старая
  // панель «Теги» в chat-view). В редизайне теги писались только в память
  // вкладки и терялись после F5.
  const persistTags = useCallback(
    (chatId: string, nextTags: string[]) => {
      setChatInternalTags(chatId, nextTags);
      if (!accountId) return;
      void internalApi
        .updateChatTags(accountId, chatId, nextTags, wsUrl)
        .then((response) => setChatInternalTags(chatId, response.tags))
        .catch(() => {
          void internalApi
            .getChatTags(accountId, chatId, wsUrl)
            .then((response) => setChatInternalTags(chatId, response.tags))
            .catch(() => undefined);
        });
    },
    [accountId, setChatInternalTags, wsUrl]
  );

  // Заметка по юзеру: серверная, одна на чат, общая для всех саппортов
  // аккаунта (/api/internal/chat-note). Локальный редактор с сохранением.
  const [note, setNote] = useState<{ chatId: string; text: string } | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteEditing, setNoteEditing] = useState(false);
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || tab !== "info" || !selectedChatId || !accountId) {
      return;
    }
    const chatId = selectedChatId;
    let cancelled = false;
    setNoteEditing(false);
    setNoteError(null);
    void internalApi
      .getChatNote(accountId, chatId, wsUrl)
      .then((response) => {
        if (!cancelled) {
          setNote({ chatId, text: response.note });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNote({ chatId, text: "" });
          setNoteError("Не удалось загрузить заметку.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, tab, selectedChatId, accountId, wsUrl]);
  const saveNote = useCallback(() => {
    if (!selectedChatId || !accountId) return;
    const chatId = selectedChatId;
    const text = noteDraft.trim();
    setNoteBusy(true);
    setNoteError(null);
    void internalApi
      .updateChatNote(accountId, chatId, text, wsUrl)
      .then((response) => {
        setNote({ chatId, text: response.note });
        setNoteEditing(false);
      })
      .catch((cause: unknown) => {
        setNoteError(cause instanceof Error ? cause.message : "Не удалось сохранить заметку.");
      })
      .finally(() => setNoteBusy(false));
  }, [accountId, noteDraft, selectedChatId, wsUrl]);

  // Отложенные подгружаем при открытии вкладки.
  useEffect(() => {
    if (open && tab === "scheduled" && selectedChatId) {
      loadScheduledMessages(selectedChatId);
    }
  }, [open, tab, selectedChatId, loadScheduledMessages]);

  // Инфо — подтягиваем подробный профиль (ID/username/телефон), как это делала
  // старая карточка профиля: теперь эти поля живут прямо в табе «Инфо».
  useEffect(() => {
    if (open && tab === "info" && selectedChatId) {
      requestProfile(selectedChatId);
    }
  }, [open, tab, selectedChatId, requestProfile]);

  // --- Блок «Статус клиента» (Трекер депозитов) -----------------------------
  const [trackerState, setTrackerState] = useState<TrackerPanelState>({ kind: "idle" });
  const [trackerStatuses, setTrackerStatuses] = useState<TrackerOption[]>([]);
  const [trackerError, setTrackerError] = useState<string | null>(null);
  const [trackerBusy, setTrackerBusy] = useState(false);
  const [trackerModal, setTrackerModal] = useState<{
    mode: "create" | "deposit";
    clientId?: number;
    prefill: TrackerModalPrefill;
  } | null>(null);

  const chatIsGroup = chat?.isGroupChat ?? false;
  const chatIsSaved = chat?.isSavedMessages ?? false;
  const chatUserId = chat?.telegramUserId;
  const profileUserId = profile?.telegramUserId;

  // Резолв клиента в админ-панели: при смене чата или дозагрузке профиля
  // проверяем, заведён ли собеседник как клиент трекера. Групповые/сервисные
  // чаты не проверяем — карточек клиентов у них нет.
  useEffect(() => {
    if (!open || tab !== "info" || !selectedChatId) {
      return;
    }
    if (chatIsGroup || chatIsSaved) {
      setTrackerState({ kind: "idle" });
      return;
    }
    const digitFallback = /^\d+$/.test(selectedChatId) ? Number(selectedChatId) : undefined;
    const telegramId = profileUserId ?? chatUserId ?? digitFallback;
    if (telegramId === undefined) {
      setTrackerState({ kind: "loading" });
      return;
    }
    let cancelled = false;
    setTrackerState({ kind: "loading" });
    setTrackerError(null);
    void (async () => {
      try {
        const ctx = await loadTrackerContextOnce(wsUrl);
        if (cancelled) return;
        setTrackerStatuses(ctx.statuses);
        if (!ctx.enabled || !ctx.hasBinding) {
          setTrackerState({ kind: "disabled" });
          return;
        }
        const result = await trackerApi.resolve(wsUrl, {
          telegramId,
          username: null,
          geoId: null
        });
        if (cancelled) return;
        const target: TrackerClient | undefined = result.matches.find(
          (match) => match.clientId > 0
        );
        if (target) {
          setTrackerState({
            kind: "present",
            clientId: target.clientId,
            status: target.status
          });
        } else {
          setTrackerState({ kind: "absent" });
        }
      } catch (cause) {
        if (cancelled) return;
        // Сеть/сервер отвалились — не бесим саппорта, просто прячем блок.
        setTrackerError(cause instanceof Error ? cause.message : "Не удалось проверить клиента");
        setTrackerState({ kind: "idle" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab, selectedChatId, chatIsGroup, chatIsSaved, chatUserId, profileUserId, wsUrl]);

  const changeTrackerStatus = useCallback(
    async (nextStatus: number): Promise<void> => {
      if (trackerState.kind !== "present") return;
      const previous = trackerState;
      const clientId = trackerState.clientId;
      // Оптимистично: сразу двигаем select — без «Подтвердить/Сохранить»,
      // как просил заказчик. На ошибке — откат и локальное сообщение.
      setTrackerState({ kind: "present", clientId, status: nextStatus });
      setTrackerBusy(true);
      setTrackerError(null);
      const opId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try {
        await trackerApi.updateStatus(wsUrl, clientId, {
          externalOperationId: opId,
          status: nextStatus
        });
      } catch (cause) {
        setTrackerState(previous);
        setTrackerError(cause instanceof Error ? cause.message : "Не удалось обновить статус");
      } finally {
        setTrackerBusy(false);
      }
    },
    [trackerState, wsUrl]
  );

  const openTrackerModal = useCallback(
    (mode: "create" | "deposit") => {
      if (!selectedChatId || !chat) return;
      const digitFallback = /^\d+$/.test(selectedChatId) ? Number(selectedChatId) : undefined;
      const telegramId = profileUserId ?? chatUserId ?? digitFallback;
      const prefill: TrackerModalPrefill = {
        telegramId: telegramId !== undefined ? String(telegramId) : "",
        username: profile?.username ?? "",
        nickname: chat.title ?? "",
        country: profile?.phoneNumberCountry ?? ""
      };
      if (mode === "deposit" && trackerState.kind === "present") {
        setTrackerModal({ mode, clientId: trackerState.clientId, prefill });
      } else {
        setTrackerModal({ mode: "create", prefill });
      }
    },
    [selectedChatId, chat, profileUserId, chatUserId, profile, trackerState]
  );

  const refreshTrackerAfterSave = useCallback((): void => {
    if (!selectedChatId) return;
    const digitFallback = /^\d+$/.test(selectedChatId) ? Number(selectedChatId) : undefined;
    const telegramId = profileUserId ?? chatUserId ?? digitFallback;
    if (telegramId === undefined) return;
    void (async () => {
      try {
        const result = await trackerApi.resolve(wsUrl, {
          telegramId,
          username: null,
          geoId: null
        });
        const target = result.matches.find((match) => match.clientId > 0);
        if (target) {
          setTrackerState({
            kind: "present",
            clientId: target.clientId,
            status: target.status
          });
        }
      } catch {
        // Блок обновится при следующем открытии чата.
      }
    })();
  }, [selectedChatId, profileUserId, chatUserId, wsUrl]);

  // Вкладка «Оплата» — тот же список, но всегда фильтр payment + плоский режим.
  useEffect(() => {
    if (!open) return;
    if (tab === "payments") {
      trust.setFilterKind("payment");
      trust.setNav({ mode: "all" });
    } else if (tab === "templates" && trust.filterKind === "payment") {
      trust.setFilterKind("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  // Ресайз левой границы: тянем влево → панель шире.
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const onResizeStart = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      resizeRef.current = { startX: event.clientX, startWidth: width };
      (event.currentTarget as HTMLDivElement).setPointerCapture(event.pointerId);
    },
    [width]
  );
  const onResizeMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!resizeRef.current) return;
      const { startX, startWidth } = resizeRef.current;
      // Панель справа: движение курсора влево (delta<0) увеличивает ширину.
      const next = Math.min(
        RIGHT_PANEL_WIDTH_MAX,
        Math.max(RIGHT_PANEL_WIDTH_MIN, startWidth - (event.clientX - startX) / uiScale)
      );
      setWidth(next);
    },
    [setWidth, uiScale]
  );
  const onResizeEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    resizeRef.current = null;
    try {
      (event.currentTarget as HTMLDivElement).releasePointerCapture(event.pointerId);
    } catch {
      /* pointer capture уже снят */
    }
  }, []);

  if (!open || !selectedChatId || !chat || chat.isSavedMessages) {
    return null;
  }

  const title = chat.title;
  // Та же логика, что в шапке чата (app.tsx): с датой/временем последнего
  // входа. Раньше здесь был голый ярлык «был(а) в сети» без даты, а при
  // неизвестном статусе — выдуманное «был(а) недавно».
  const statusLabel = chat.isBlockedByUser
    ? "Заблокировал"
    : chat.isPeerOnline
      ? chat.peerStatusLabel ?? "в сети"
      : formatPeerStatus(chat.peerLastSeenAt, chat.peerStatusLabel, timeZone);
  const folderTitles = chat.listIds
    .filter((id) => id !== "main" && id !== "archive")
    .map((id) => dialogLists[id]?.title)
    .filter((value): value is string => Boolean(value));
  const tags = chat.internalTags;
  // ID пользователя. Приоритет — данные профиля/диалога; если их ещё нет,
  // для личного чата берём сам chatId: в TDLib id личного чата равен
  // Telegram-ID собеседника (у групп/каналов единого user-id нет — там поле
  // скрыто, как и в старой карточке).
  const privateChatUserId =
    !chat.isGroupChat && !chat.isSavedMessages && /^\d+$/.test(selectedChatId)
      ? Number(selectedChatId)
      : undefined;
  const chatTelegramId = profile?.telegramUserId ?? chat.telegramUserId ?? privateChatUserId;

  const trustBadge = (kind: string): string =>
    kind === "photo"
      ? "Фото"
      : kind === "video"
        ? "Видео"
        : kind === "voice"
          ? "Голосовое"
          : kind === "link"
            ? "Ссылка"
            : kind === "payment"
              ? "Реквизит"
              : "Документ";

  const countSectionDeep = (sectionId: string): number =>
    (trust.sectionCounts.counts.get(sectionId) ?? 0) +
    trust.sections
      .filter((s) => s.parentId === sectionId)
      .reduce((sum, child) => sum + (trust.sectionCounts.counts.get(child.sectionId) ?? 0), 0);

  const buildOverride = (
    asset: (typeof trust.filteredAssets)[number],
    text: string
  ): MessageContent =>
    asset.content.kind === "text"
      ? { kind: "text", text }
      : ({ ...asset.content, caption: text } as MessageContent);

  const renderTrustCard = (asset: (typeof trust.filteredAssets)[number]) => {
    const previewUrl =
      asset.content.kind === "photo" || asset.content.kind === "video"
        ? asset.content.url ??
          asset.content.thumbnailUrl ??
          (asset.content.fileId
            ? `${httpBaseFromWsUrl(trust.wsUrl)}/api/media/file/${encodeURIComponent(asset.content.fileId)}`
            : undefined)
        : undefined;
    const extraCount = asset.extraContents?.length ?? 0;
    const usedEntry = trust.usage[asset.assetId];
    const editing = templateEditor?.assetId === asset.assetId ? templateEditor : null;
    const audioContent =
      asset.content.kind === "voice" || asset.content.kind === "videoNote"
        ? asset.content
        : null;
    const isAudio = audioContent !== null;
    // Описание шаблона: явное description, у голосовых/кружков без него —
    // подпись или расшифровка. Внутренняя заметка (supportDescription) —
    // отдельной строкой, как в старом траст-центре: у голосовых нет превью,
    // и без этих строк саппорт не понимает, что отправляет.
    const audioFallback =
      audioContent && audioContent.kind === "voice"
        ? audioContent.caption?.trim() || audioContent.transcript?.trim()
        : audioContent?.transcript?.trim();
    const description = asset.description?.trim() || audioFallback || "";
    const supportNote = asset.supportDescription?.trim() ?? "";
    return (
      <div className="tpl-card-wrap" key={asset.assetId}>
        <div className="tpl-card">
          <span className="tpl-card__icon">
            {previewUrl ? (
              <img src={previewUrl} alt={asset.title} loading="lazy" decoding="async" />
            ) : (
              <i className={isAudio ? "ic ic-mic" : "ic ic-copy"} aria-hidden="true" />
            )}
            {extraCount > 0 ? <span className="tpl-card__extra">+{extraCount}</span> : null}
          </span>
          <span className="tpl-card__copy">
            <span className="tpl-card__name">
              {asset.title}
              <em className="tpl-card__kind">{trustBadge(asset.mediaKind)}</em>
            </span>
            <span className="tpl-card__preview" title={description || undefined}>
              {description || "Без описания"}
            </span>
            {supportNote ? (
              <span className="tpl-card__support" title={supportNote}>
                {supportNote}
              </span>
            ) : null}
            {usedEntry ? (
              <span
                className="tpl-card__trust"
                title={`${
                  usedEntry.source === "manual" ? "Пометка вручную" : "Отправляли из шаблонов"
                } · ${formatFullDateTime(usedEntry.lastUsedAt, timeZone)}${
                  usedEntry.actorLogin ? ` · ${usedEntry.actorLogin}` : ""
                }`}
              >
                <i className="ic ic-card" aria-hidden="true" /> Отправляли в этом чате
                {usedEntry.usedCount > 1 ? ` · ${usedEntry.usedCount}×` : ""}
              </span>
            ) : null}
          </span>
          <span className="tpl-card__acts">
            <button
              type="button"
              className="tpl-card__mark"
              onClick={() => void trust.toggleUsed(asset.assetId, !usedEntry)}
              disabled={!selectedChatId || trust.usageBusyId === asset.assetId}
              title={usedEntry ? "Снять пометку" : "Пометить как отправленный"}
            >
              {usedEntry ? "Снять" : "Пометить"}
            </button>
            <button
              type="button"
              className="tpl-card__edit"
              onClick={() =>
                setTemplateEditor({
                  assetId: asset.assetId,
                  text:
                    asset.content.kind === "text"
                      ? asset.content.text
                      : (asset.content as { caption?: string }).caption ?? ""
                })
              }
              disabled={!selectedChatId}
              title="Изменить перед отправкой"
            >
              Изменить
            </button>
            <button
              type="button"
              className="tpl-card__send"
              onClick={() => trust.send(asset)}
              disabled={!selectedChatId || trust.isSending}
            >
              Отправить
            </button>
          </span>
        </div>
        {editing ? (
          <div className="tpl-edit">
            <textarea
              autoFocus
              value={editing.text}
              onChange={(event) =>
                setTemplateEditor({ assetId: asset.assetId, text: event.target.value })
              }
              rows={3}
              placeholder="Текст перед отправкой"
            />
            <div className="tpl-edit__acts">
              <button
                type="button"
                className="tpl-card__mark"
                onClick={() => setTemplateEditor(null)}
              >
                Отмена
              </button>
              <button
                type="button"
                className="tpl-card__send"
                onClick={() => {
                  trust.send(asset, buildOverride(asset, editing.text));
                  setTemplateEditor(null);
                }}
                disabled={trust.isSending}
              >
                Отправить
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderTrustBody = () => {
    if (trust.isLoading) {
      return <div className="side-empty"><div className="side-empty__title">Загружаем…</div></div>;
    }
    if (trust.error) {
      return <div className="side-empty"><div className="side-empty__title">{trust.error}</div></div>;
    }
    // Вкладка «Оплата» — плоский список реквизитов, без навигации по разделам.
    if (tab === "payments") {
      return trust.filteredAssets.length === 0 ? (
        <div className="side-empty">
          <div className="side-empty__icon">
            <i className="ic ic-card" aria-hidden="true" />
          </div>
          <div className="side-empty__title">Способы оплаты не добавлены</div>
        </div>
      ) : (
        <div className="tpl-list">{trust.filteredAssets.map(renderTrustCard)}</div>
      );
    }
    const currentSectionId = trust.nav.mode === "browse" ? trust.nav.sectionId : null;
    const currentSection = currentSectionId
      ? trust.sections.find((s) => s.sectionId === currentSectionId) ?? null
      : null;
    const atRoot = trust.nav.mode === "browse" && currentSectionId === null;
    // Дочерние разделы текущего уровня (корень → корневые, иначе подразделы).
    const childSections =
      trust.nav.mode === "browse"
        ? currentSectionId === null
          ? trust.rootSections
          : trust.sections.filter((s) => s.parentId === currentSectionId)
        : [];
    const breadcrumb =
      trust.nav.mode === "all"
        ? "Все шаблоны"
        : trust.nav.mode === "unsorted"
          ? "Без раздела"
          : currentSection
            ? currentSection.title
            : null;
    return (
      <>
        {!atRoot ? (
          <div className="tpl-navrow">
            <button
              type="button"
              className="tpl-back"
              onClick={() =>
                trust.setNav({
                  mode: "browse",
                  sectionId: currentSection ? currentSection.parentId : null
                })
              }
            >
              <i className="ic ic-back" aria-hidden="true" /> Назад
            </button>
            {breadcrumb ? <span className="tpl-breadcrumb">{breadcrumb}</span> : null}
          </div>
        ) : null}
        {trust.hasSections ? (
          <div className="tpl-sections">
            {atRoot ? (
              <button
                type="button"
                className="tpl-section-tile"
                onClick={() => trust.setNav({ mode: "all" })}
              >
                Все шаблоны
              </button>
            ) : null}
            {childSections.map((section) => (
              <button
                key={section.sectionId}
                type="button"
                className="tpl-section-tile"
                onClick={() => trust.setNav({ mode: "browse", sectionId: section.sectionId })}
              >
                <i className="ic ic-folder" aria-hidden="true" />
                {section.title}
                <span className="tpl-section-tile__count">{countSectionDeep(section.sectionId)}</span>
              </button>
            ))}
            {atRoot && trust.sectionCounts.orphans > 0 ? (
              <button
                type="button"
                className="tpl-section-tile"
                onClick={() => trust.setNav({ mode: "unsorted" })}
              >
                <i className="ic ic-folder" aria-hidden="true" />
                Без раздела
                <span className="tpl-section-tile__count">{trust.sectionCounts.orphans}</span>
              </button>
            ) : null}
          </div>
        ) : null}
        {trust.assetsVisible ? (
          trust.filteredAssets.length === 0 ? (
            childSections.length === 0 ? (
              <div className="side-empty">
                <div className="side-empty__icon">
                  <i className="ic ic-copy" aria-hidden="true" />
                </div>
                <div className="side-empty__title">
                  {trust.assets.length === 0 ? "Шаблонов пока нет" : "Ничего не найдено"}
                </div>
              </div>
            ) : null
          ) : (
            <div className="tpl-list">{trust.filteredAssets.map(renderTrustCard)}</div>
          )
        ) : null}
      </>
    );
  };

  return (
    <aside className="side-panel" style={{ width: `${width}px` }}>
      <div
        className="side-panel__resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="Ширина правой панели"
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
      />
      <nav className="side-panel__tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            className={
              tab === item.id
                ? "side-panel__tab side-panel__tab--active"
                : "side-panel__tab"
            }
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className="side-panel__body">
        {tab === "info" ? (
          <div className="info-tab">
            <div className="info-tab__hero">
              <ChatAvatar
                title={title}
                avatarUrl={chat.avatarUrl}
                className={`info-tab__avatar ${avatarGradientClass(selectedChatId)}`}
              />
              <div className="info-tab__ident">
                {chatTelegramId !== undefined ? (
                  <div className="info-id">
                    <span className="info-id__label">ID</span>
                    <code className="info-id__value">{chatTelegramId}</code>
                    <button
                      type="button"
                      className="info-id__copy"
                      title="Копировать ID"
                      aria-label="Копировать ID"
                      onClick={() => void copyTextToClipboard(String(chatTelegramId))}
                    >
                      <i className="ic ic-copy" aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
                <div className="info-tab__name">
                  {title}
                </div>
                {statusLabel ? <div className="info-tab__status">{statusLabel}</div> : null}
                <div className="info-tab__badges">
                  {profile?.phoneNumberCountry ? (
                    <span className="info-tab__badge">
                      {countryLabel(profile.phoneNumberCountry)}
                    </span>
                  ) : null}
                  {folderTitles.map((name) => (
                    <span key={name} className="info-tab__badge info-tab__badge--folder">
                      <i className="ic ic-folder" aria-hidden="true" /> {name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="info-list">
              {!isSupportRole ? (
              <div className="info-row">
                <div className="info-row__label">Имя пользователя</div>
                <div className="info-row__value">
                  {profile?.username ? `@${profile.username}` : "Не указано"}
                  {profile?.username ? (
                    <button
                      type="button"
                      className="info-row__copy"
                      title="Копировать"
                      aria-label="Копировать имя пользователя"
                      onClick={() => void copyTextToClipboard(`@${profile.username}`)}
                    >
                      <i className="ic ic-copy" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
              ) : null}
              {!isSupportRole ? (
              <div className="info-row">
                <div className="info-row__label">Телефон</div>
                <div className="info-row__value">
                  {profile?.phoneNumber ? `+${profile.phoneNumber}` : "Не указан"}
                  {profile?.phoneNumber ? (
                    <button
                      type="button"
                      className="info-row__copy"
                      title="Копировать"
                      aria-label="Копировать телефон"
                      onClick={() => void copyTextToClipboard(`+${profile.phoneNumber ?? ""}`)}
                    >
                      <i className="ic ic-copy" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
              ) : null}
              {!isSupportRole && profile?.bio ? (
                <div className="info-row">
                  <div className="info-row__label">О себе</div>
                  <div className="info-row__value">{profile.bio}</div>
                </div>
              ) : null}
              <div className="info-row">
                <div className="info-row__label">Язык</div>
                <div className="info-row__value">
                  {profile?.phoneNumberCountry
                    ? countryLabel(profile.phoneNumberCountry)
                    : "Не определён"}
                </div>
              </div>
              {(() => {
                // Таймер автоудаления: и выставленный нами, и выставленный
                // юзером; «Выключено» снимает таймер. Актуальный статус — из
                // live-чата, профиль — фолбэк.
                const currentAutoDelete = chat.autoDeleteTime ?? profile?.autoDeleteTime ?? 0;
                return (
                  <div className="info-row">
                    <label className="info-row__label" htmlFor="info-auto-delete">
                      Автоудаление сообщений
                    </label>
                    <div className="info-row__value">
                      <select
                        id="info-auto-delete"
                        className="info-row__select"
                        value={String(currentAutoDelete)}
                        onChange={(event) =>
                          setChatAutoDeleteTime(selectedChatId, Number(event.target.value))
                        }
                      >
                        {AUTO_DELETE_OPTIONS.map((option) => (
                          <option key={option.value} value={String(option.value)}>
                            {option.label}
                          </option>
                        ))}
                        {currentAutoDelete > 0 &&
                        !AUTO_DELETE_OPTIONS.some((option) => option.value === currentAutoDelete) ? (
                          <option value={String(currentAutoDelete)}>
                            {Math.round(currentAutoDelete / 86_400)} дн.
                          </option>
                        ) : null}
                      </select>
                    </div>
                  </div>
                );
              })()}
            </div>

            {trackerState.kind === "loading" ? (
              <div className="tracker-panel tracker-panel--loading">
                Проверяем в трекере…
              </div>
            ) : null}
            {trackerState.kind === "present" ? (
              <div className="tracker-panel">
                <div className="tracker-panel__head">
                  <i className="ic ic-user" aria-hidden="true" />
                  <span className="tracker-panel__title">Статус клиента</span>
                  {/* № карточки (запрос №27): тот же номер, что в отказе «уже есть
                      в базе», — по нему клиент ищется в админке и в трекере. */}
                  <span
                    className="tracker-panel__card"
                    title="Номер карточки клиента в базе админ-панели"
                  >
                    карточка #{trackerState.clientId}
                  </span>
                </div>
                <div className="tracker-panel__row">
                  <select
                    className="tracker-panel__select"
                    value={String(trackerState.status)}
                    onChange={(event) => void changeTrackerStatus(Number(event.target.value))}
                    disabled={trackerBusy || trackerStatuses.length === 0}
                    aria-label="Статус клиента"
                  >
                    {trackerStatuses.map((option) => (
                      <option key={option.value} value={String(option.value)}>
                        {option.label}
                      </option>
                    ))}
                    {!trackerStatuses.some((option) => option.value === trackerState.status) ? (
                      <option value={String(trackerState.status)}>#{trackerState.status}</option>
                    ) : null}
                  </select>
                  <button
                    type="button"
                    className="tracker-panel__btn"
                    onClick={() => openTrackerModal("deposit")}
                  >
                    <i className="ic ic-card" aria-hidden="true" /> Трекер депозитов
                  </button>
                </div>
                {trackerError ? (
                  <div className="tracker-panel__error">{trackerError}</div>
                ) : null}
              </div>
            ) : null}
            {trackerState.kind === "absent" ? (
              <div className="tracker-panel">
                <div className="tracker-panel__head">
                  <i className="ic ic-user" aria-hidden="true" />
                  <span className="tracker-panel__title">Статус клиента</span>
                </div>
                <div className="tracker-panel__row">
                  <span className="tracker-panel__hint">Клиент не заведён в трекер</span>
                  <button
                    type="button"
                    className="tracker-panel__btn tracker-panel__btn--primary"
                    onClick={() => openTrackerModal("create")}
                  >
                    <i className="ic ic-plus" aria-hidden="true" /> Занести
                  </button>
                </div>
              </div>
            ) : null}

            {!chat.isGroupChat && !chat.isSavedMessages && chatTelegramId !== undefined ? (
              <ClientBotWalletPanel telegramId={chatTelegramId} wsUrl={wsUrl} />
            ) : null}

            <div className="info-block">
              <div className="info-block__head">
                <span className="info-block__title">Теги</span>
                <button
                  type="button"
                  className="info-block__add"
                  onClick={() => setTagInputOpen((value) => !value)}
                >
                  <i className="ic ic-plus" aria-hidden="true" /> Добавить тег
                </button>
              </div>
              {tagInputOpen ? (
                <form
                  className="tag-add"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const next = tagDraft.trim();
                    if (!next || tags.includes(next)) {
                      setTagDraft("");
                      return;
                    }
                    persistTags(selectedChatId, [...tags, next]);
                    setTagDraft("");
                  }}
                >
                  <input
                    autoFocus
                    value={tagDraft}
                    onChange={(event) => setTagDraft(event.target.value)}
                    placeholder="Название тега, Enter"
                  />
                </form>
              ) : null}
              <div className="tag-chips">
                {tags.length > 0 ? (
                  tags.map((t) => (
                    <span key={t} className="tag-chip">
                      {t}
                      <button
                        type="button"
                        className="tag-chip__remove"
                        aria-label={`Удалить тег ${t}`}
                        onClick={() =>
                          persistTags(
                            selectedChatId,
                            tags.filter((existing) => existing !== t)
                          )
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))
                ) : (
                  <span className="tag-chip tag-chip--empty">Тегов пока нет</span>
                )}
              </div>
            </div>

            <div className="info-block">
              <div className="info-block__head">
                <span className="info-block__title">Заметки</span>
                {!noteEditing ? (
                  <button
                    type="button"
                    className="info-block__add"
                    onClick={() => {
                      setNoteDraft(note?.chatId === selectedChatId ? note.text : "");
                      setNoteEditing(true);
                    }}
                    title={note?.text ? "Изменить заметку" : "Добавить заметку"}
                    aria-label={note?.text ? "Изменить заметку" : "Добавить заметку"}
                  >
                    <i className="ic ic-edit" aria-hidden="true" />{" "}
                    {note?.text ? "Изменить" : "Добавить"}
                  </button>
                ) : null}
              </div>
              {noteEditing ? (
                <form
                  className="info-note"
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveNote();
                  }}
                >
                  <textarea
                    autoFocus
                    className="info-note__textarea"
                    value={noteDraft}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        setNoteEditing(false);
                      } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                        event.preventDefault();
                        saveNote();
                      }
                    }}
                    placeholder="Заметка о пользователе — видна всем саппортам этого аккаунта"
                    rows={4}
                    maxLength={4000}
                    disabled={noteBusy}
                  />
                  <div className="info-note__actions">
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => setNoteEditing(false)}
                      disabled={noteBusy}
                    >
                      Отмена
                    </button>
                    <button type="submit" className="button" disabled={noteBusy}>
                      {noteBusy ? "Сохраняем…" : "Сохранить"}
                    </button>
                  </div>
                </form>
              ) : (
                <div
                  className={
                    note?.chatId === selectedChatId && note.text
                      ? "info-block__note"
                      : "info-block__note info-block__note--empty"
                  }
                >
                  {note?.chatId === selectedChatId
                    ? note.text || "Заметки пока нет"
                    : "Загружаем…"}
                </div>
              )}
              {noteError ? <div className="info-note__error">{noteError}</div> : null}
            </div>

            <button
              type="button"
              className={mediaOpen ? "info-media info-media--open" : "info-media"}
              onClick={() => setMediaOpen((value) => !value)}
              aria-expanded={mediaOpen}
              title={mediaOpen ? "Скрыть общие медиа" : "Показать фото, видео, файлы и ссылки из чата"}
            >
              <span className="info-media__icon">
                <i className="ic ic-saved" aria-hidden="true" />
              </span>
              <span className="info-media__text">Общие медиа</span>
              <i className="ic ic-arrow-down info-media__chevron" aria-hidden="true" />
            </button>
            {mediaOpen ? <ChatMediaSection chatId={selectedChatId} /> : null}
          </div>
        ) : null}

        {tab === "templates" ? (
          <div className="tpl-tab">
            <div className="side-search">
              <i className="ic ic-search" aria-hidden="true" />
              <input
                placeholder="Поиск шаблонов"
                value={trust.filterQuery}
                onChange={(event) => trust.setFilterQuery(event.target.value)}
              />
            </div>
            <div className="side-filters">
              {(
                [
                  ["all", "Все"],
                  ["photo", "Фото"],
                  ["video", "Видео"],
                  ["document", "Документы"],
                  ["voice", "Голосовые"],
                  ["link", "Ссылки"]
                ] as [TrustFilterKind, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={
                    trust.filterKind === value ? "side-filter side-filter--active" : "side-filter"
                  }
                  onClick={() => trust.setFilterKind(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {renderTrustBody()}
            {isAdmin ? (
              <button type="button" className="side-cta" onClick={() => openTrustManage()}>
                <i className="ic ic-plus" aria-hidden="true" /> Управление шаблонами
              </button>
            ) : null}
          </div>
        ) : null}

        {tab === "scheduled" ? (
          <div className="sched-tab">
            <div className="side-search">
              <i className="ic ic-search" aria-hidden="true" />
              <input placeholder="Поиск отложенных" />
            </div>
            <div className="side-filters">
              {["Все", "Сегодня", "Завтра", "На этой неделе"].map((f, i) => (
                <button
                  key={f}
                  type="button"
                  className={i === 0 ? "side-filter side-filter--active" : "side-filter"}
                >
                  {f}
                </button>
              ))}
            </div>
            {scheduled.length > 0 ? (
              <div className="sched-list">
                {scheduled.map((msg) => (
                  <div key={msg.id} className="sched-card">
                    <span className="sched-card__icon">
                      <i className="ic ic-schedule" aria-hidden="true" />
                    </span>
                    <span className="sched-card__copy">
                      <span className="sched-card__time">
                        {msg.sendAt
                          ? formatFullDateTime(msg.sendAt, timeZone)
                          : "Когда в сети"}
                      </span>
                      <span className="sched-card__text">{msg.text || "Вложение"}</span>
                    </span>
                    <span className="sched-card__acts">
                      <button
                        type="button"
                        className="sched-card__act"
                        aria-label="Действия"
                        title="Действия"
                        onClick={(event) => {
                          const rect = event.currentTarget.getBoundingClientRect();
                          setScheduledMenu({
                            x: rect.right,
                            y: rect.bottom,
                            messageId: msg.id
                          });
                        }}
                      >
                        <i className="ic ic-more" aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="side-empty">
                <div className="side-empty__icon">
                  <i className="ic ic-schedule" aria-hidden="true" />
                </div>
                <div className="side-empty__title">Здесь будут ваши отложенные сообщения</div>
                <div className="side-empty__text">
                  Запланируйте сообщение, чтобы оно отправилось автоматически
                </div>
              </div>
            )}
            <button type="button" className="side-cta">
              <i className="ic ic-plus" aria-hidden="true" /> Запланировать сообщение
            </button>
          </div>
        ) : null}

        {tab === "deleted" ? (
          <div className="del-tab">
            <div className="side-search">
              <i className="ic ic-search" aria-hidden="true" />
              <input placeholder="Поиск удалённых" />
            </div>
            <div className="side-filters">
              {["Все", "Сегодня", "На этой неделе", "На этом месяце"].map((f, i) => (
                <button
                  key={f}
                  type="button"
                  className={i === 0 ? "side-filter side-filter--active" : "side-filter"}
                >
                  {f}
                </button>
              ))}
            </div>
            {deletedMessages.length > 0 ? (
              <div className="sched-list">
                {deletedMessages.map((msg) => (
                  <div key={msg.id} className="sched-card">
                    <span className="sched-card__icon">
                      <i className="ic ic-delete" aria-hidden="true" />
                    </span>
                    <span className="sched-card__copy">
                      <span className="sched-card__time">
                        {msg.deletedAt
                          ? formatFullDateTime(msg.deletedAt, timeZone)
                          : formatFullDateTime(msg.occurredAt, timeZone)}
                      </span>
                      <span className="sched-card__text">
                        {msg.text?.trim() || "Вложение"}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="side-empty">
                <div className="side-empty__icon">
                  <i className="ic ic-delete" aria-hidden="true" />
                </div>
                <div className="side-empty__title">Здесь отображаются удалённые сообщения</div>
                <div className="side-empty__text">
                  Сообщения хранятся 30 дней, после чего удаляются окончательно
                </div>
              </div>
            )}
          </div>
        ) : null}

        {tab === "payments" ? (
          <div className="pay-tab">
            <div className="side-search">
              <i className="ic ic-search" aria-hidden="true" />
              <input
                placeholder="Поиск способа оплаты"
                value={trust.filterQuery}
                onChange={(event) => trust.setFilterQuery(event.target.value)}
              />
            </div>
            {renderTrustBody()}
          </div>
        ) : null}
      </div>
      {scheduledMenu && selectedChatId
        ? createPortal(
            <div
              className="dialog-context-menu__layer scheduled-modal__menu-layer"
              onClick={() => setScheduledMenu(null)}
              onContextMenu={(event) => {
                event.preventDefault();
                setScheduledMenu(null);
              }}
              role="presentation"
            >
              <ContextMenuPositioner anchorX={scheduledMenu.x} anchorY={scheduledMenu.y}>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    sendScheduledNow(selectedChatId, scheduledMenu.messageId);
                    setScheduledMenu(null);
                  }}
                >
                  <i className="ic ic-send" aria-hidden="true" />
                  Отправить сейчас
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item"
                  onClick={() => {
                    const entry = scheduled.find((m) => m.id === scheduledMenu.messageId);
                    setScheduledPicker({
                      messageId: scheduledMenu.messageId,
                      initialAt: entry?.sendAt
                    });
                    setScheduledMenu(null);
                  }}
                >
                  <i className="ic ic-schedule" aria-hidden="true" />
                  Изменить время
                </button>
                <div className="dialog-context-menu__divider" />
                <button
                  type="button"
                  role="menuitem"
                  className="dialog-context-menu__item dialog-context-menu__item--danger"
                  onClick={() => {
                    if (window.confirm("Удалить отложенное сообщение?")) {
                      deleteScheduledMessage(selectedChatId, scheduledMenu.messageId);
                    }
                    setScheduledMenu(null);
                  }}
                >
                  <i className="ic ic-delete" aria-hidden="true" />
                  Удалить
                </button>
              </ContextMenuPositioner>
            </div>,
            document.body
          )
        : null}
      {scheduledPicker && selectedChatId ? (
        <SchedulePickerModal
          title="Изменить время"
          submitLabel="Перенести"
          initialAt={scheduledPicker.initialAt}
          timeZone={timeZone}
          allowWhenOnline={Boolean(chat && !chat.isGroupChat && !chat.isSavedMessages)}
          onClose={() => setScheduledPicker(null)}
          onSubmit={(result) => {
            rescheduleMessage(selectedChatId, scheduledPicker.messageId, result);
            setScheduledPicker(null);
          }}
        />
      ) : null}
      {trackerModal ? (
        <DepositTrackerModal
          wsUrl={wsUrl}
          onClose={() => setTrackerModal(null)}
          onSaved={refreshTrackerAfterSave}
          initialMode={trackerModal.mode}
          initialPrefill={trackerModal.prefill}
          {...(trackerModal.clientId !== undefined
            ? { initialClientId: trackerModal.clientId }
            : {})}
        />
      ) : null}
    </aside>
  );
};

export default ChatSidePanel;
